import { useState, useCallback, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import type { AdminAuthorFileItem } from "@/types/article";
import type { PaginationMeta } from "@/types/common";
import type { UsePaginationReturn } from "@/hooks/use-pagination";
import {
  getAuthorFilesColumns,
  type AuthorFileAction,
} from "./author-files-columns";
import { ArticlePagination } from "./article-pagination";
import { SyncDialog } from "./sync-dialog";

interface AuthorFilesTableProps {
  data: AdminAuthorFileItem[];
  meta: PaginationMeta;
  pagination: UsePaginationReturn;
}

export function AuthorFilesTable({
  data,
  meta,
  pagination,
}: AuthorFilesTableProps) {
  const [selectedFile, setSelectedFile] =
    useState<AdminAuthorFileItem | null>(null);
  const [showSyncDialog, setShowSyncDialog] = useState(false);

  const handleAction = useCallback(
    (file: AdminAuthorFileItem, action: AuthorFileAction) => {
      if (action === "sync") {
        setSelectedFile(file);
        setShowSyncDialog(true);
      }
    },
    []
  );

  const handleDialogClose = useCallback(() => {
    setShowSyncDialog(false);
    setSelectedFile(null);
  }, []);

  const columns = useMemo(
    () => getAuthorFilesColumns(handleAction),
    [handleAction]
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (data.length === 0) {
    return (
      <div className="rounded-lg border p-8 text-center">
        <p className="text-muted-foreground">
          작성자 파일이 없습니다.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-md border overflow-auto max-h-[calc(100vh-280px)]">
        <Table>
          <TableHeader className="sticky top-0 bg-card z-10">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                className="hover:bg-muted/50 transition-colors duration-75"
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(
                      cell.column.columnDef.cell,
                      cell.getContext()
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <ArticlePagination meta={meta} pagination={pagination} />

      {selectedFile && (
        <SyncDialog
          file={selectedFile}
          open={showSyncDialog}
          onOpenChange={(open) => {
            if (!open) handleDialogClose();
          }}
        />
      )}
    </div>
  );
}
